import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
  RetryContext
} from '@microsoft/signalr';
import { REALTIME_CONFIG, REALTIME_TOKEN_PROVIDER } from './realtime.config';
import { RealtimeEnvelope } from './realtime-envelope';

export type RealtimeConnectionState = 'offline' | 'connecting' | 'connected' | 'reconnecting';

/**
 * The single method the server pushes on — RealtimeConstants.ClientMethods.Receive server-side.
 * Application code should subscribe to `envelopes$` rather than naming this.
 */
const RECEIVE_METHOD = 'Receive';

/**
 * Retries for as long as there is a session to retry for. SignalR's default gives up after
 * ~30s; a user who leaves a tab open over lunch would come back to a dead feed with no signal.
 */
const retryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds(context: RetryContext): number {
    const backoff = Math.min(30_000, 1_000 * Math.pow(2, context.previousRetryCount));
    return backoff + Math.floor(Math.random() * 1_000); // jitter
  }
};

/**
 * Owns the single authenticated WebSocket to the realtime hub.
 *
 * Deliberately minimal for this first cut: connect (token in the query string), reconnect,
 * expose the connection state, and offer a generic `on`/`invoke` plus an `echo` helper. The
 * topic model and typed events are added later, on top of this.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeConnection {
  readonly #config = inject(REALTIME_CONFIG);
  readonly #tokens = inject(REALTIME_TOKEN_PROVIDER);

  #hub: HubConnection | null = null;
  #starting: Promise<void> | null = null;

  // Method name -> live handlers. Held here and not only on the HubConnection, because the
  // connection does not exist until start() builds it and is discarded on logout. A root-level
  // subscriber starts listening at bootstrap, long before login opens the socket; without this
  // registry its handler would attach to null and never fire.
  readonly #handlers = new Map<string, Set<(payload: any) => void>>();

  readonly #state = signal<RealtimeConnectionState>('offline');
  readonly state = this.#state.asReadonly();
  readonly isConnected = computed(() => this.#state() === 'connected');
  readonly connectionId = signal<string | null>(null);

  async start(): Promise<void> {
    if (!this.#config.enabled) return;

    // No token means no session. Attempting anyway produces a 401 the retry policy would
    // hammer forever; staying closed until there is a token is the correct wait.
    if (!this.#tokens.getToken()) return;

    if (this.#hub?.state === HubConnectionState.Connected) return;
    if (this.#starting) return this.#starting;

    this.#hub ??= this.#build();
    this.#state.set('connecting');

    this.#starting = this.#hub
      .start()
      .then(() => {
        this.#state.set('connected');
        this.connectionId.set(this.#hub?.connectionId ?? null);
      })
      .catch(error => {
        this.#state.set('offline');
        throw error;
      })
      .finally(() => {
        this.#starting = null;
      });

    return this.#starting;
  }

  async stop(): Promise<void> {
    if (!this.#hub) return;
    const hub = this.#hub;
    this.#hub = null;
    this.connectionId.set(null);
    this.#state.set('offline');
    await hub.stop().catch(() => undefined);
  }

  /**
   * Every event the server pushes. Consumers route on `envelope.type` and narrow `payload` to
   * whatever that event carries — the transport method name stays in this file.
   */
  get envelopes$(): Observable<RealtimeEnvelope> {
    return this.on<RealtimeEnvelope>(RECEIVE_METHOD);
  }

  /**
   * Register a handler for a server-pushed method (e.g. "ReceiveEcho"). Safe to call before the
   * connection is up: the handler is attached to whatever connection is built next.
   */
  on<T = unknown>(method: string): Observable<T> {
    return new Observable<T>(observer => {
      const handler = (payload: T) => observer.next(payload);
      const handlers = this.#handlers.get(method) ?? new Set();
      handlers.add(handler);
      this.#handlers.set(method, handlers);
      this.#hub?.on(method, handler);

      return () => {
        this.#handlers.get(method)?.delete(handler);
        this.#hub?.off(method, handler);
      };
    });
  }

  /** Call a hub method. Rejects if the connection is not up. */
  invoke<T = void>(method: string, ...args: unknown[]): Promise<T> {
    if (this.#hub?.state !== HubConnectionState.Connected) {
      return Promise.reject(new Error(`Realtime: cannot invoke '${method}' while ${this.#state()}.`));
    }
    return this.#hub.invoke<T>(method, ...args);
  }

  /** Convenience for the review: send a message and get the server's echo back via ReceiveEcho. */
  echo(message: string): Promise<void> {
    return this.invoke('Echo', message);
  }

  #build(): HubConnection {
    const hub = new HubConnectionBuilder()
      .withUrl(this.#config.hubUrl, {
        // Re-read on every negotiate and reconnect, so a refreshed token is picked up and a
        // stale one is never reused. On a WebSocket handshake this token rides in the query
        // string — which is exactly what the server's OnMessageReceived handler reads.
        accessTokenFactory: () => this.#tokens.getToken() ?? ''
      })
      .withAutomaticReconnect(retryPolicy)
      .configureLogging(this.#config.verbose ? LogLevel.Information : LogLevel.Warning)
      .build();

    hub.onreconnecting(() => this.#state.set('reconnecting'));
    hub.onreconnected(id => {
      this.#state.set('connected');
      this.connectionId.set(id ?? null);
    });
    hub.onclose(() => {
      this.#state.set('offline');
      this.connectionId.set(null);
    });

    // Everything subscribed while there was no connection — at bootstrap, or between a logout
    // and the next login — attaches here. Automatic reconnects reuse this same object, so they
    // keep their handlers without passing through again.
    for (const [method, handlers] of this.#handlers) {
      for (const handler of handlers) hub.on(method, handler);
    }

    return hub;
  }
}
